import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// App-wide hover hints (docs/DESIGN_SYSTEM.md §8.9). Mounted once in _app.
//
// Instead of wrapping every button in a <Tooltip>, this listens at the
// document and upgrades what controls ALREADY declare:
//   - `data-tooltip="…"` on anything — the explicit opt-in
//   - `title` on a button / link / [role=button]
//   - `aria-label` on an ICON-ONLY button / link (no visible text), since
//     that label is exactly what a sighted user can't see
// After HOVER_DELAY_MS of a mouse resting on it (or keyboard focus), a small
// styled label appears above it (below if there's no room). The native
// `title` is parked in `data-tooltip-title` while ours shows so the browser's
// own grey box doesn't double up, and restored on leave.
//
// Opt out with `data-tooltip-off` (e.g. a whole-row overlay link whose label
// just repeats the row).
//
// An ancestor can steer the hints inside it: `data-tooltip-placement="right"`
// opens them beside the element instead of above it, and `data-tooltip-delay`
// (ms) shortens the wait. The collapsed sidebar rail uses both — above would
// cover the next icon up, and an unlabelled icon shouldn't make you wait.
const HOVER_DELAY_MS = 500;
const GAP = 6;
const EDGE = 8;

const CANDIDATE =
  '[data-tooltip], button[title], a[title], [role="button"][title], button[aria-label], a[aria-label], [role="button"][aria-label]';

function inherited(el, attr) {
  return el.closest(`[${attr}]`)?.getAttribute(attr) ?? null;
}

function tooltipTextFor(el) {
  if (!el || el.closest('[data-tooltip-off]')) return null;
  const explicit = el.getAttribute('data-tooltip');
  if (explicit) return explicit;
  const title = el.getAttribute('title') || el.getAttribute('data-tooltip-title');
  if (title) return title;
  const label = el.getAttribute('aria-label');
  const hasVisibleText = (el.textContent || '').trim().length > 0;
  return label && !hasVisibleText ? label : null;
}

export default function HoverTooltips() {
  const [tip, setTip] = useState(null); // { text, placement, rect }
  const [pos, setPos] = useState(null);
  const bubble = useRef(null);
  const timer = useRef(null);
  const active = useRef(null);

  useEffect(() => {
    const restoreTitle = (el) => {
      const parked = el?.getAttribute('data-tooltip-title');
      if (parked != null) {
        el.setAttribute('title', parked);
        el.removeAttribute('data-tooltip-title');
      }
    };
    const hide = () => {
      clearTimeout(timer.current);
      restoreTitle(active.current);
      active.current = null;
      setTip(null);
      setPos(null);
    };
    const schedule = (el) => {
      if (el === active.current) return;
      hide();
      const text = tooltipTextFor(el);
      if (!text) return;
      active.current = el;
      // Park the native title right away, not after the delay — otherwise the
      // browser's own tooltip can win the race at roughly the same moment.
      const title = el.getAttribute('title');
      if (title != null) {
        el.setAttribute('data-tooltip-title', title);
        el.removeAttribute('title');
      }
      const placement = inherited(el, 'data-tooltip-placement');
      // Not `Number(attr)` straight: a missing attribute is null, and
      // Number(null) is 0 — every hint would fire instantly.
      const delayAttr = inherited(el, 'data-tooltip-delay');
      const delay = delayAttr == null ? NaN : Number(delayAttr);
      timer.current = setTimeout(() => {
        if (active.current !== el || !el.isConnected) return;
        setTip({ text, placement, rect: el.getBoundingClientRect() });
      }, delay >= 0 ? delay : HOVER_DELAY_MS);
    };

    const onOver = (e) => {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      const el = e.target.closest?.(CANDIDATE);
      if (el) schedule(el);
      else if (active.current && !active.current.contains(e.target)) hide();
    };
    const onOut = (e) => {
      if (active.current && !active.current.contains(e.relatedTarget)) hide();
    };
    const onFocusIn = (e) => {
      const el = e.target.closest?.(CANDIDATE);
      if (el && el.matches(':focus-visible')) schedule(el);
    };

    document.addEventListener('pointerover', onOver);
    document.addEventListener('pointerout', onOut);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', hide);
    document.addEventListener('pointerdown', hide, true);
    document.addEventListener('keydown', hide, true);
    window.addEventListener('scroll', hide, true);
    return () => {
      hide();
      document.removeEventListener('pointerover', onOver);
      document.removeEventListener('pointerout', onOut);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', hide);
      document.removeEventListener('pointerdown', hide, true);
      document.removeEventListener('keydown', hide, true);
      window.removeEventListener('scroll', hide, true);
    };
  }, []);

  // Position after the bubble has rendered and can be measured.
  useLayoutEffect(() => {
    if (!tip || !bubble.current) return;
    const { width, height } = bubble.current.getBoundingClientRect();
    const { rect } = tip;
    if (tip.placement === 'right') {
      const centeredY = rect.top + rect.height / 2 - height / 2;
      setPos({
        top: Math.min(Math.max(centeredY, EDGE), window.innerHeight - height - EDGE),
        left: rect.right + GAP,
      });
      return;
    }
    const above = rect.top - GAP - height;
    const top = above >= EDGE ? above : rect.bottom + GAP;
    const centered = rect.left + rect.width / 2 - width / 2;
    const left = Math.min(Math.max(centered, EDGE), window.innerWidth - width - EDGE);
    setPos({ top, left });
  }, [tip]);

  if (!tip || typeof document === 'undefined') return null;
  return createPortal(
    <div
      ref={bubble}
      role='tooltip'
      style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999 }}
      className='pointer-events-none fixed z-[1000] max-w-xs whitespace-pre-line rounded-md bg-obsidian px-2 py-1 text-2xs font-medium text-white shadow-lg dark:bg-text-light dark:text-obsidian'
    >
      {tip.text}
    </div>,
    document.body
  );
}
